import { getWizardConfig } from "@/config/wizardConfig";
import { Goal } from "@/types/goals";

const CURRENT_YEAR = new Date().getFullYear();

export interface GoalCalculationResult {
  year: number;
  amount: number;
  projectedAmount: number;
}

export const calculateYearAndAmount = (
  dateOfBirth: string,
  goalType: string,
): GoalCalculationResult => {
  const birthDate = new Date(dateOfBirth);
  const birthYear = birthDate.getFullYear();

  const { education, eligibility } = getWizardConfig();

  // Expected start year is birth year + minEducationAge
  const expectedStartYear = birthYear + eligibility.minEducationAge;

  // Added capping
  const targetYear = Math.max(expectedStartYear, CURRENT_YEAR);

  const baseAmount =
    goalType === "CollegeFund"
      ? education.baseCollegeCost
      : education.baseTradeschoolCost;

  const yearDiff = targetYear - education.baselineYear;
  const projectedAmount = Math.round(
    yearDiff <= 0
      ? baseAmount
      : baseAmount * Math.pow(education.inflationRate, yearDiff),
  );

  return {
    year: targetYear,
    amount: projectedAmount,
    projectedAmount: projectedAmount,
  };
};

export const calculateMonthlyContribution = (
  amount: number,
  targetYear: number,
): number => {
  const targetDate = new Date(targetYear, 11, 31);
  const currentDate = new Date();
  const monthsDiff = Math.max(
    1,
    (targetDate.getFullYear() - currentDate.getFullYear()) * 12 +
      (targetDate.getMonth() - currentDate.getMonth()),
  );

  return Math.round(amount / monthsDiff);
};

export const recalculateGoalOnPurposeChange = (
  goal: Goal,
  newPurpose: string,
): Partial<Goal> => {
  const calculation = calculateYearAndAmount(
    goal.dateOfBirth ?? "",
    newPurpose,
  );
  return {
    purpose: newPurpose,
    year: calculation.year,
    amount: calculation.amount,
    projectedAmount: calculation.projectedAmount,
    monthlyContribution: calculateMonthlyContribution(
      calculation.amount,
      calculation.year,
    ),
  };
};

export const recalculateGoalOnYearChange = (
  goal: Goal,
  newYear: number,
): Partial<Goal> => {
  const { education } = getWizardConfig();
  const yearDiff = newYear - education.baselineYear;
  const baseAmount =
    goal.purpose === "CollegeFund"
      ? education.baseCollegeCost
      : education.baseTradeschoolCost;
  const projectedAmount = Math.round(
    yearDiff <= 0
      ? baseAmount
      : baseAmount * Math.pow(education.inflationRate, yearDiff),
  );

  return {
    year: newYear,
    amount: projectedAmount, // Reset amount to match new projected amount when year changes
    projectedAmount: projectedAmount,
    monthlyContribution: calculateMonthlyContribution(projectedAmount, newYear),
  };
};
